import { FieldProps } from './types'

import * as React from 'react';
import { forwardRef } from 'react';

import { Stack, FormControl, FormHelperText, Typography, type SxProps, useTheme } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateField as MuiDateField } from '@mui/x-date-pickers/DateField';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FieldValues, RegisterOptions } from 'react-hook-form';

interface DateFieldTextProps<T extends FieldValues = FieldValues>
  extends Omit<FieldProps<T>, 'startAdornment'> {
  name: string
  label?: string
  placeholder?: string
  defaultValue?: string
  type?: string
  helperText?: string
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  labelAdornment?: React.ReactNode
  rules?: RegisterOptions
  readOnly?: boolean
  sx?: SxProps
  onValueChange?: (value: string) => void
}

const DateFieldImpl = forwardRef<HTMLInputElement, DateFieldTextProps>(({
  label,
  name,
  placeholder,
  startAdornment,
  endAdornment,
  rules,
  register,
  control,
  helperText,
  formState: { errors },
  readOnly = false,
  defaultValue = '',
  type = 'text',
  sx,
  onValueChange,
}, ref) => {

  const theme = useTheme()
  const registerProps = register(name, rules);

   const vera_theme ={
    borderRadius: 2,
    border: `1px solid  ${theme.palette.primary[50]}`,
    fontSize: 16,
    "input": {
      padding: 3,
    },
    "&:hover": {
      border: `1px solid ${theme.palette.brand[600]}` // Change border color when input is hovered
    }
  }

       //   <DemoContainer components={['DateField']}>
    //     {/* <MuiDateField sx={vera_theme} /> */}
    //   </DemoContainer> 
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl error={!!errors[name]} fullWidth>
        <Stack mb={2} direction="row" spacing={2}>
          {label && (
            <Typography variant="body1">
              {label}
            </Typography>
          )}
          {helperText && <FormHelperText>({helperText})</FormHelperText>}
        </Stack>
        <MuiDateField 
          ref={ref}
          disablePast 
          size="small" 
          variant="outlined"
          format="MM/YYYY"
          onChange={(value) => {
            if (onValueChange) {
              onValueChange(value ? value.toString() : '');
            }
            // Convert the value to an event-like object for react-hook-form
            const event = { target: { value: value ? value.toString() : '' } };
            registerProps.onChange(event);
          }}
          onBlur={registerProps.onBlur}
          sx={{
            "& fieldset": { 
              border: `none !important`,
            },
            input: {
              border: `1px solid ${theme.palette.grey[100]}`,
              height: 28,
              minWidth: '100%',
              borderRadius: 2,
              fontSize: 16,
              backgroundColor: theme.palette.grey[100],
              cursor: 'pointer',
              ':hover':{
                border: `1px solid ${theme.palette.brand[500]}`,
                backgroundColor: theme.palette.grey[200],
              },
            }
          }}
        />
        {errors[name] && <FormHelperText sx={{ margin: '4px 0 0' }}>{errors[name]?.message as string}</FormHelperText>}
      </FormControl>
    </LocalizationProvider>
  )
})

/**
 * Re-exported under the generic signature so a caller's own form type flows through.
 * See the `FieldProps` docblock in `./types` for why the implementation above stays on the
 * default instantiation and why this assertion is the standard idiom rather than a
 * suppression. `forwardRef` erases type parameters, so the ref is restored by hand here.
 */
export const DateField = DateFieldImpl as <T extends FieldValues = FieldValues>(
  props: DateFieldTextProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement | null
